import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Paperclip, StickyNote, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import LinkedNoteDialog from './LinkedNoteDialog';
import HorsePickerInline from './HorsePickerInline';
import MultiDatePicker from './MultiDatePicker';
import AttachmentPickerSheet from '../shared/AttachmentPickerSheet';

function localNow() {
  const now = new Date();
  return {
    date: now.toLocaleDateString('sv'),
    time: now.toTimeString().slice(0, 5),
  };
}

export default function TravelLogModal({ open, onClose, onSaved, horse, locations, scheduledItem, availableHorses }) {
  const { date: todayDate, time: nowTime } = localNow();
  const [attachPickerOpen, setAttachPickerOpen] = useState(false);

  const initForm = () => ({
    travel_from: '',
    travel_to: '',
    log_date: todayDate,
    log_time: nowTime,
    performed_by: '',
    billable: false,
    cost: '',
    cost_currency: 'USD',
    comments: '',
    publish_to_owner_portal: false,
  });

  const [form, setForm]                         = useState(initForm);
  const [direction, setDirection]               = useState('');   // 'to_farm' | 'from_farm' | 'other'
  const [toFarmLocationId, setToFarmLocationId] = useState('');
  const [destType, setDestType]                 = useState('boarding_farm'); // 'boarding_farm' | 'with_trainer' | 'other'
  const [destEntityId, setDestEntityId]         = useState('');
  const [destOtherText, setDestOtherText]       = useState('');
  const [boardingFacilities, setBoardingFacilities] = useState([]);
  const [trainerFacilities, setTrainerFacilities]   = useState([]);
  const [saving, setSaving]                     = useState(false);
  const [attachments, setAttachments]           = useState([]);
  const [uploading, setUploading]               = useState(false);
  const [showNote, setShowNote]                 = useState(false);
  const [selectedHorseIds, setSelectedHorseIds] = useState([]);
  const [dates, setDates]                       = useState([]);

  useEffect(() => {
    if (open) {
      const { date: d, time: t } = localNow();
      setForm(initForm());
      setAttachments([]);
      setShowNote(false);
      setDates([{ id: Math.random().toString(36).slice(2), date: d, time: t }]);
      // Pre-fill from scheduled travel item
      if (scheduledItem?.travel_direction) {
        setDirection(scheduledItem.travel_direction);
        setDestType(scheduledItem.travel_dest_type || 'boarding_farm');
        setDestEntityId(scheduledItem.travel_dest_entity_id || '');
        setDestOtherText(scheduledItem.travel_dest_other || '');
        if (scheduledItem.start_date) {
          setDates([{ id: Math.random().toString(36).slice(2), date: scheduledItem.start_date, time: scheduledItem.preferred_time || t }]);
        }
        setForm(p => ({ ...p, performed_by: scheduledItem.notes || '' }));
      } else {
        setDirection('');
        setToFarmLocationId('');
        setDestType('boarding_farm');
        setDestEntityId('');
        setDestOtherText('');
      }
      api.get('/crm-entities').then(res => {
        const all = res.data || [];
        setBoardingFacilities(all.filter(e => ['farm_boarding', 'farm'].includes(e.organization_industry_role)));
        setTrainerFacilities(all.filter(e => e.organization_industry_role === 'trainer' || e.person_industry_role === 'trainer'));
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const getTravelName = () => {
    if (direction === 'to_farm') {
      const locName = (locations || []).find(l => l.id === toFarmLocationId)?.name;
      const dest = locName ? `On Farm (${locName})` : 'On Farm';
      return `${form.travel_from || 'Origin'} → ${dest}`;
    }
    if (direction === 'from_farm') {
      let destName = '';
      if (destType === 'boarding_farm') {
        destName = boardingFacilities.find(bf => bf.id === destEntityId)?.system_name || 'Destination';
      } else if (destType === 'with_trainer') {
        destName = trainerFacilities.find(tf => tf.id === destEntityId)?.system_name || 'Destination';
      } else {
        destName = destOtherText || 'Destination';
      }
      return `On Farm → ${destName}`;
    }
    return [form.travel_from, form.travel_to].filter(Boolean).join(' → ') || 'Travel';
  };

  const updateHorseLocation = async (horseIds) => {
    if (!direction || direction === 'other') return;
    let update = {};
    if (direction === 'to_farm') {
      const locName = (locations || []).find(l => l.id === toFarmLocationId)?.name || null;
      update = { boarding_type: 'on_farm', boarding_location_id: null, boarding_notes: null, farmfolio_location_id: toFarmLocationId || null, farmfolio_location_name: locName };
    } else if (direction === 'from_farm') {
      if (destType === 'boarding_farm' || destType === 'with_trainer') {
        update = { boarding_type: destType, boarding_location_id: destEntityId || null, boarding_notes: null };
      } else {
        update = { boarding_type: 'other', boarding_location_id: null, boarding_notes: destOtherText || null };
      }
    }
    if (Object.keys(update).length > 0) {
      await Promise.allSettled(horseIds.map(hid => api.put(`/horses/${hid}`, update)));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files.map(file => ({ name: file.name, file }))]);
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!dates.length) { toast.error('Date is required'); return; }
    if (!direction) { toast.error('Please select a travel direction'); return; }
    if (direction === 'from_farm') {
      if ((destType === 'boarding_farm' || destType === 'with_trainer') && !destEntityId) {
        toast.error('Please select a destination'); return;
      }
      if (destType === 'other' && !destOtherText.trim()) {
        toast.error('Please enter a destination'); return;
      }
    }
    const isMulti = Array.isArray(availableHorses);
    if (isMulti && selectedHorseIds.length === 0) { toast.error('Select at least one horse'); return; }

    setSaving(true);
    try {
      const basePayload = {
        horse_id: horse?.id,
        scheduled_care_id: scheduledItem?.id || null,
        category: 'Travel',
        type: 'Transport',
        name: getTravelName(),
        performed_by: form.performed_by,
        performed_by_type: 'In-house',
        comments: form.comments || '',
        billable: form.billable,
        cost: form.billable && form.cost ? parseFloat(form.cost) : null,
        cost_currency: form.cost_currency || 'USD',
        publish_to_owner_portal: form.publish_to_owner_portal,
      };

      if (scheduledItem?.id) {
        await api.post(`/farmfolio/scheduled-care/${scheduledItem.id}/complete`, {
          ...basePayload, log_date: dates[0].date, log_time: dates[0].time || '',
        });
        if (horse?.id) await updateHorseLocation([horse.id]);
      } else {
        for (const dateEntry of dates) {
          const payload = { ...basePayload, log_date: dateEntry.date, log_time: dateEntry.time || '' };
          if (isMulti) {
            await api.post('/farmfolio/global/bulk/care-log', { horse_ids: selectedHorseIds, payload });
          } else {
            await api.post('/farmfolio/care-log', payload);
          }
        }
        if (isMulti) await updateHorseLocation(selectedHorseIds);
        else if (horse?.id) await updateHorseLocation([horse.id]);
      }

      if (attachments.length > 0) {
        setUploading(true);
        await Promise.allSettled(
          attachments.map(({ file }) => {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('horse_id', horse?.id);
            fd.append('description', `Travel — ${dates[0].date}${dates[0].time ? ' @ ' + dates[0].time : ''}`);
            return api.post('/files/upload', fd);
          })
        );
        setUploading(false);
      }

      const count = scheduledItem?.id ? 1 : dates.length;
      toast.success(scheduledItem?.id ? 'Care completed' : `Travel logged${count > 1 ? ` (${count} entries)` : ''}`);
      onSaved();
      onClose();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const DIRECTION_BTNS = [
    { value: 'to_farm',   label: 'Returning to Farm' },
    { value: 'from_farm', label: 'Leaving Farm' },
    { value: 'other',     label: 'Other Travel' },
  ];

  const destEntities = destType === 'boarding_farm' ? boardingFacilities : trainerFacilities;
  const careRef = dates[0]?.date
    ? `Travel — ${dates[0].date}${dates[0].time ? ' @ ' + dates[0].time : ''}`
    : 'Travel care entry';

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{scheduledItem ? 'Complete Care' : 'Travel'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {Array.isArray(availableHorses) && (
              <HorsePickerInline availableHorses={availableHorses}
                value={selectedHorseIds} onChange={setSelectedHorseIds} mode="multi" locations={locations} />
            )}

            {/* Travel Details — indigo card matching ScheduleCareModal style */}
            <div className="space-y-3 border rounded p-3 bg-indigo-50/30">
              <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Travel Details</p>

              {/* Direction */}
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Direction</Label>
                <div className="flex gap-1">
                  {DIRECTION_BTNS.map(btn => (
                    <button key={btn.value} type="button"
                      onClick={() => setDirection(btn.value)}
                      className={`flex-1 py-1.5 px-2 rounded text-xs font-medium border transition-colors ${
                        direction === btn.value
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                      data-testid={`travel-direction-${btn.value}`}>
                      {btn.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Returning to Farm */}
              {direction === 'to_farm' && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">From</Label>
                    <Input className="h-8 text-xs" value={form.travel_from} onChange={e => f('travel_from', e.target.value)}
                      placeholder="Origin (e.g. Keeneland)" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">To</Label>
                    <div className="rounded border px-3 py-1.5 text-xs bg-muted/20 text-muted-foreground">On Farm</div>
                  </div>
                  {(locations || []).length > 0 && (
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold">Location on Farm</Label>
                      <Select value={toFarmLocationId || '__none__'} onValueChange={v => setToFarmLocationId(v === '__none__' ? '' : v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select location…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— None —</SelectItem>
                          {(locations || []).map(l => (
                            <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}

              {/* Leaving Farm */}
              {direction === 'from_farm' && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">Destination Type</Label>
                    <Select value={destType} onValueChange={v => { setDestType(v); setDestEntityId(''); setDestOtherText(''); }}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boarding_farm">Boarding Farm</SelectItem>
                        <SelectItem value="with_trainer">With Trainer</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {(destType === 'boarding_farm' || destType === 'with_trainer') && (
                    <Select value={destEntityId || '__none__'} onValueChange={v => setDestEntityId(v === '__none__' ? '' : v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={destType === 'boarding_farm' ? 'Select farm…' : 'Select trainer…'} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Select —</SelectItem>
                        {destEntities.map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.system_name || e.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {destType === 'other' && (
                    <Input className="h-8 text-xs" value={destOtherText} onChange={e => setDestOtherText(e.target.value)}
                      placeholder="e.g. Keeneland Racetrack" />
                  )}
                </>
              )}

              {/* Other Travel */}
              {direction === 'other' && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">To</Label>
                    <Input className="h-8 text-xs" value={form.travel_to} onChange={e => f('travel_to', e.target.value)}
                      placeholder="Destination" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">From</Label>
                    <Input className="h-8 text-xs" value={form.travel_from} onChange={e => f('travel_from', e.target.value)}
                      placeholder="Origin" />
                  </div>
                </>
              )}

              {/* Vanned By */}
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Vanned By</Label>
                <Input className="h-8 text-xs" value={form.performed_by} onChange={e => f('performed_by', e.target.value)}
                  placeholder="e.g. Van Co." />
              </div>
            </div>

            {/* Date & Time */}
            <div className="space-y-1">
              <Label className="font-semibold">Date & Time</Label>
              <MultiDatePicker value={dates} onChange={setDates} defaultTime={localNow().time} />
            </div>

            {/* Billable toggle + Cost */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Switch checked={form.billable} onCheckedChange={v => f('billable', v)} />
                <Label className="cursor-pointer font-semibold">Billable</Label>
              </div>
              {form.billable && (
                <div className="space-y-1">
                  <Label className="font-semibold">Cost</Label>
                  <div className="flex gap-2">
                    <Select value={form.cost_currency || 'USD'} onValueChange={v => f('cost_currency', v)}>
                      <SelectTrigger className="w-20 shrink-0"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['USD','EUR','GBP','CAD','AUD','NZD','ZAR','CHF','JPY','HKD'].map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input type="number" min="0" step="0.01" value={form.cost}
                      onChange={e => f('cost', e.target.value)} placeholder="0.00" className="flex-1" />
                  </div>
                </div>
              )}
            </div>

            {/* Comments */}
            <div className="space-y-1">
              <Label className="font-semibold">Comments</Label>
              <Textarea rows={3} value={form.comments} onChange={e => f('comments', e.target.value)} />
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <Button type="button" variant="outline" className="w-full justify-start gap-2"
                onClick={() => setAttachPickerOpen(true)}>
                <Paperclip className="w-4 h-4" /> Upload Attachments
              </Button>
              {attachments.length > 0 && (
                <div className="space-y-1">
                  {attachments.map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-muted-foreground bg-muted/30 rounded px-2 py-1">
                      <span className="truncate">{a.name}</span>
                      <Button variant="ghost" size="icon" className="h-4 w-4 shrink-0"
                        onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Linked Note */}
            <div>
              <Button type="button" variant="outline" className="w-full justify-start gap-2"
                onClick={() => setShowNote(true)}>
                <StickyNote className="w-4 h-4" /> Create Linked Note
              </Button>
            </div>

            {/* Publish to Owner Portal */}
            <div className="flex items-center gap-2">
              <Switch checked={form.publish_to_owner_portal} onCheckedChange={v => f('publish_to_owner_portal', v)} />
              <Label className="cursor-pointer text-sm">Publish to Owner Portal</Label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t mt-2">
            <Button variant="outline" onClick={onClose} disabled={saving || uploading}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || uploading}
              className="bg-primary hover:bg-primary/90 text-white">
              {saving || uploading ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <LinkedNoteDialog
        open={showNote}
        onClose={() => setShowNote(false)}
        onSaved={() => setShowNote(false)}
        horse={horse}
        careEntryRef={careRef}
      />
      <AttachmentPickerSheet
        open={attachPickerOpen}
        onClose={() => setAttachPickerOpen(false)}
        onFiles={(files) => setAttachments(prev => [...prev, ...files.map(f => ({ name: f.name, file: f }))])}
      />
    </>
  );
}
